import React, { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Paperclip } from "lucide-react";

const TIME_SLOTS = ["7AM - 9AM", "9AM - 11AM", "11AM - 1PM", "1PM - 2PM", "2PM - 4PM"];

export function BookingForm({ user, onSubmitted }) {
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    employee_id: user?.member_id || "",
    phone: user?.mobile || "",
    dob: "",
    age: "",
    corporate: user?.company || "",
    pan: "",
    aadhaar_last4: "",
    consent: false,
    address: "",
    pincode: "",
    payment_made: "no",
    payment_method: "upi",
    payment_details: "",
    preferred_date: "",
    preferred_time: TIME_SLOTS[0],
    remark: "",
  });
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDob = (v) => {
    let age = "";
    if (v) { const d = new Date(v), n = new Date(); age = n.getFullYear() - d.getFullYear() - ((n.getMonth() < d.getMonth() || (n.getMonth() === d.getMonth() && n.getDate() < d.getDate())) ? 1 : 0); age = age >= 0 ? String(age) : ""; }
    setForm((f) => ({ ...f, dob: v, age }));
  };

  const uploadShot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bookings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.file_id);
      toast.success("Payment screenshot attached");
    } catch (err) {
      toast.error("Couldn't upload the screenshot. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.pincode || !form.preferred_date) {
      toast.error("Please fill name, phone, address, pincode and preferred day.");
      return;
    }
    if (!form.consent) {
      toast.error("Please accept the data-use consent (DPDP) to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        payment_made: form.payment_made === "yes",
        payment_screenshot_file_id: fileId,
      });
      setDone(data);
      toast.success(`Booking ${data.booking_number} confirmed!`);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-form-done" className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        🩺 Booking <b>{done.booking_number}</b> confirmed! Lupin Diagnostics will conduct the home collection on {done.preferred_date} ({done.preferred_time}) and share the report.
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="booking-form"
      className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-5 shadow-sm space-y-3">
      <p className="font-heading font-semibold text-emerald-950">Book a Home Health Check-up</p>
      <p className="text-[11px] text-zinc-500 -mt-1">Lupin Diagnostics will conduct the home collection and provide the report.</p>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Name</Label>
          <Input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Employee ID</Label>
          <Input data-testid="booking-empid" value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} /></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Email</Label>
          <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Phone number</Label>
          <Input data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="9876543210" /></div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Date of birth</Label>
          <Input data-testid="booking-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={(e) => setDob(e.target.value)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Age</Label>
          <Input data-testid="booking-age" value={form.age} readOnly placeholder="auto" className="bg-zinc-50" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Corporate</Label>
          <Input data-testid="booking-corporate" value={form.corporate} onChange={(e) => set("corporate", e.target.value)} placeholder="Company" /></div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5 col-span-2"><Label className="text-xs">PAN <span className="text-zinc-400">(KYC)</span></Label>
          <Input data-testid="booking-pan" value={form.pan} maxLength={10} onChange={(e) => set("pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Aadhaar last 4</Label>
          <Input data-testid="booking-aadhaar" value={form.aadhaar_last4} maxLength={4} onChange={(e) => set("aadhaar_last4", e.target.value.replace(/\D/g, ""))} placeholder="1234" /></div>
      </div>

      <div className="space-y-1.5"><Label className="text-xs">Home Collection - Pin Code</Label>
        <Input data-testid="booking-pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)} placeholder="560001" /></div>

      <div className="space-y-1.5"><Label className="text-xs">Home Collection Address</Label>
        <Textarea data-testid="booking-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Flat / House, Street, Area, City" /></div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Preferred Day (within 24 hrs)</Label>
          <Input data-testid="booking-date" type="date" min={new Date().toISOString().slice(0, 10)} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Preferred Time (7AM - 4PM)</Label>
          <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
            <SelectTrigger data-testid="booking-time"><SelectValue /></SelectTrigger>
            <SelectContent>{TIME_SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Payment Made?</Label>
          <Select value={form.payment_made} onValueChange={(v) => set("payment_made", v)}>
            <SelectTrigger data-testid="booking-payment-made"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Payment Details</Label>
          <Input data-testid="booking-payment-details" value={form.payment_details} onChange={(e) => set("payment_details", e.target.value)} placeholder="UPI / txn ref" /></div>
      </div>

      <div className="space-y-1.5"><Label className="text-xs">Payment method</Label>
        <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
          <SelectTrigger data-testid="booking-payment-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="upi">Pay via UPI QR (scan & upload screenshot)</SelectItem>
            <SelectItem value="razorpay">Pay online (Razorpay)</SelectItem>
          </SelectContent>
        </Select></div>

      {form.payment_method === "upi" ? (
      <>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-3" data-testid="booking-payment-qr">
        <img src="/assets/payment-qr.jpg" alt="Scan to pay" className="h-24 w-24 rounded-md bg-white object-contain border border-zinc-200" />
        <div className="text-xs text-emerald-800">
          <p className="font-semibold">Scan to pay</p>
          <p className="text-[11px] text-emerald-700 mt-0.5">Scan this QR with any UPI app to pay for your check-up, then set "Payment Made? = Yes" and upload the screenshot below.</p>
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50/60 p-3">
        <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" data-testid="booking-screenshot-input" onChange={uploadShot} />
        <button type="button" data-testid="booking-upload-screenshot" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-white border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-100 transition-colors disabled:opacity-60">
          <Paperclip size={16} />
          {uploading ? "Uploading…" : fileId ? "Screenshot attached ✓" : "Upload payment screenshot"}
        </button>
      </div>
      </>
      ) : (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-800" data-testid="booking-razorpay-note">
        <p className="font-semibold">Pay online (Razorpay)</p>
        <p className="text-[11px] text-emerald-700 mt-0.5">After you confirm, our team will share a secure Razorpay payment link on your email/WhatsApp. You can also buy a check-up package to pay instantly online.</p>
      </div>
      )}

      <div className="space-y-1.5"><Label className="text-xs">Remark (if any)</Label>
        <Textarea data-testid="booking-remark" rows={2} value={form.remark} onChange={(e) => set("remark", e.target.value)} placeholder="Any special instructions" /></div>

      <label className="flex items-start gap-2 text-[11px] text-zinc-600 cursor-pointer" data-testid="booking-consent-label">
        <input type="checkbox" data-testid="booking-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-emerald-700" />
        <span>I consent to Aarogya Assist collecting & processing my personal and KYC details (PAN / Aadhaar) for this service, in line with the DPDP Act, 2023.</span>
      </label>

      <Button type="submit" disabled={loading} data-testid="booking-submit"
        className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
        {loading ? "Booking…" : "Confirm Booking"}
      </Button>
    </form>
  );
}
